// Rendering Helper Functions
// HTML generation for cards, buttons, and status displays

import { truncateText } from './dataMapping';
import {
  escapeHtml,
  escapeAttr,
  roundToTwo,
  formatCurrencyDisplay,
  parseCurrencyValue,
  formatMeatOnBoneValue,
  getMeatBadgeColor,
  sanitizePercentage,
  sanitizeCurrency,
  cap,
} from './formatting';
import { collectLotImages, __isArchived } from './image';
import { ensureSettings } from './state';
import { isLotClosed } from './utils';
import { getEffectiveCloseTime } from './storage';
import {
  calculateMeatOnBone,
  calculateWalkAwayRange,
  classifyLotBidPosition,
} from './lotOperations';
import { getLifecycleStatus, computeRealizedProfit } from './lifecycle';
import { Lot, State } from './types';

/**
 * Check if lot has any pricing data
 */
export function hasPricingData(lot: Lot): boolean {
  if (!lot || typeof lot !== 'object') return false;
  if (lot.lowSellValue != null || lot.highSellValue != null) return true;
  if (lot.lowSellValueRaw || lot.highSellValueRaw) return true;
  if (Array.isArray(lot.lowListings) && lot.lowListings.length > 0) return true;
  if (Array.isArray(lot.highListings) && lot.highListings.length > 0) return true;
  if (typeof lot.priceStatus === 'string' && lot.priceStatus.trim()) return true;
  if (lot.aiAnalysis) {
    return true;
  }
  return false;
}

/**
 * Format price status value for display
 */
export function formatPriceStatusValue(lot: Lot, state: State): string {
  if (!lot || typeof lot !== 'object') {
    return '';
  }
  if (state && state.pendingPriceReviewIds && lot.id && state.pendingPriceReviewIds.has(lot.id)) {
    return 'AI pricing requested…';
  }
  const aiStatus = typeof lot.aiStatus === 'string' ? lot.aiStatus.trim().toLowerCase() : '';
  if (aiStatus === 'processing' || aiStatus === 'pending') {
    return 'AI pricing in progress…';
  }
  const status = typeof lot.priceStatus === 'string' ? lot.priceStatus.trim() : '';
  if (!status) {
    return '';
  }
  const normalized = status.toLowerCase();
  if (normalized === 'successprice') {
    return 'AI pricing available';
  }
  if (normalized === 'failedprice') {
    if (lot.aiStatus === 'failed' && lot.aiError) {
      return `AI pricing failed: ${truncateText(lot.aiError, 120)}`;
    }
    return 'AI pricing unavailable';
  }
  return `AI price: ${cap(status)}`;
}

/**
 * Format price review status for display
 */
import type { PriceReviewMetadata } from '../../types/api';

export function formatPriceReviewStatus(review: PriceReviewMetadata): string {
  if (!review || typeof review !== 'object') return '';
  if (review.status === 'successPrice') {
    return 'Review: Approved';
  }
  if (review.status === 'failedPrice') {
    return 'Review: Retry requested';
  }
  return '';
}

/**
 * Format AI status text for display
 */
export function formatAiStatusText(lot: Lot): string {
  if (!lot) return '';
  const status = lot.aiStatus;
  if (status === 'processing' || status === 'pending') {
    return 'AI analysis running…';
  }
  if (status === 'failed') {
    return lot.aiError
      ? `AI analysis failed: ${truncateText(lot.aiError, 120)}`
      : 'AI analysis failed.';
  }
  if (status === 'skipped') {
    return 'AI analysis skipped.';
  }
  if (status === 'succeeded' && !lot.aiAnalysis) {
    return 'AI analysis complete.';
  }
  return '';
}

/**
 * Format AI summary text for display
 */
export function formatAiSummaryText(analysis: Record<string, unknown>): string {
  if (!analysis) return '';
  if (typeof analysis === 'string') {
    return `AI summary: ${truncateText(analysis, 160)}`;
  }
  if (typeof analysis !== 'object') {
    return `AI summary: ${truncateText(String(analysis), 160)}`;
  }
  const preferred = findFirstStringValue(analysis, 0);
  if (preferred) {
    return `AI summary: ${truncateText(preferred, 160)}`;
  }
  // Do not display raw JSON data
  return '';
}

/**
 * Recursively find first string value in analysis object
 */
export function findFirstStringValue(value: unknown, depth: number): string {
  if (depth > 3 || value == null) return '';
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const nested = findFirstStringValue(value[i], depth + 1);
      if (nested) {
        return nested;
      }
    }
    return '';
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const preferredKeys = [
      'summary',
      'result',
      'insights',
      'notes',
      'pricingNotes',
      'pricingSummary',
      'comment',
      'message',
    ];
    for (let j = 0; j < preferredKeys.length; j++) {
      const key = preferredKeys[j];
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const preferred = findFirstStringValue(obj[key], depth + 1);
        if (preferred) {
          return preferred;
        }
      }
    }
    const keys = Object.keys(obj);
    for (let k = 0; k < keys.length; k++) {
      const nestedValue = findFirstStringValue(obj[keys[k]], depth + 1);
      if (nestedValue) {
        return nestedValue;
      }
    }
  }
  return '';
}

/**
 * Build lifecycle controls for a closed lot.
 *
 * Closed auctions move through the reseller lifecycle instead of being a
 * dead end: record Won (with hammer price) or Lost, then List and Sell won
 * items. Sold/lost lots show a compact outcome badge.
 */
export function buildClosedLotButton(lot: Lot, state: State): string {
  if (!lot || !lot.id) {
    return '';
  }
  const id = escapeAttr(lot.id);
  const status = getLifecycleStatus(lot);

  const button = (action: string, label: string, title: string, variant = '') =>
    `<button type="button" class="lifecycle-button${variant}" data-action="${action}" data-id="${id}" title="${escapeAttr(
      truncateText(title, 80)
    )}" aria-label="${escapeAttr(truncateText(title, 80))}">${label}</button>`;

  if (status === 'watching' || status === 'bidding') {
    return (
      '<div class="lifecycle-controls">' +
      '<span class="lifecycle-controls__label">Closed</span>' +
      button(
        'mark-won',
        '🏆 Won',
        'I won this lot — record what I paid',
        ' lifecycle-button--won'
      ) +
      button('mark-lost', 'Lost', 'I did not win this lot', ' lifecycle-button--lost') +
      '</div>'
    );
  }

  if (status === 'won') {
    return (
      '<div class="lifecycle-controls">' +
      '<span class="lifecycle-badge lifecycle-badge--won">🏆 Won</span>' +
      button('export-lot', '📤 Sell', 'Export to eBay or copy for Facebook Marketplace') +
      button('mark-listed', 'Listed', 'I listed this item for sale') +
      button('mark-sold', '💰 Sold', 'Record the sale', ' lifecycle-button--sold') +
      '</div>'
    );
  }

  if (status === 'listed') {
    return (
      '<div class="lifecycle-controls">' +
      '<span class="lifecycle-badge lifecycle-badge--listed">📦 Listed</span>' +
      button('export-lot', '📤 Sell', 'Export to eBay or copy for Facebook Marketplace') +
      button('mark-sold', '💰 Sold', 'Record the sale', ' lifecycle-button--sold') +
      '</div>'
    );
  }

  if (status === 'sold') {
    const profit = computeRealizedProfit(lot, state.settings);
    const profitText =
      profit != null
        ? `${profit < 0 ? '−' : '+'}${formatCurrencyDisplay(Math.abs(profit)) ?? Math.abs(profit).toFixed(2)}`
        : '';
    const profitClass = profit != null && profit < 0 ? ' lifecycle-badge--loss' : '';
    return (
      '<div class="lifecycle-controls">' +
      `<span class="lifecycle-badge lifecycle-badge--sold${profitClass}" title="Realized profit (net of selling costs)">✅ Sold${
        profitText ? ' ' + profitText : ''
      }</span>` +
      '</div>'
    );
  }

  // lost / archived: muted badge + the original remove affordance
  const isPendingDelete = state.pendingFavoriteIds && state.pendingFavoriteIds.has(lot.id);
  const removeTitle = `Remove ${lot.title ? lot.title : 'lot'} from the list`;
  return (
    '<div class="lifecycle-controls">' +
    `<span class="lifecycle-badge lifecycle-badge--lost">${status === 'archived' ? 'Archived' : 'Lost'}</span>` +
    `<button type="button" class="lifecycle-button" data-action="delete-closed" data-id="${id}" title="${escapeAttr(
      truncateText(removeTitle, 80)
    )}" aria-label="${escapeAttr(truncateText(removeTitle, 80))}"${
      isPendingDelete ? ' disabled' : ''
    }>Remove</button>` +
    '</div>'
  );
}

/**
 * Build price check button HTML
 */
export function buildPriceCheckButton(
  lot: Lot,
  options: { disabled?: boolean; title?: string; ariaLabel?: string }
): string {
  if (!lot || !lot.id) {
    return '';
  }
  const opts = options || {};
  const disabled = !!opts.disabled;
  const title = opts.title || 'Request new AI price analysis (uses 1 credit)';
  const ariaLabel = opts.ariaLabel || title;
  const costHint = disabled ? '' : '<span class="price-check-button__subtext">1 credit</span>';
  return `<button type="button" class="price-check-button" data-action="price-retry" data-id="${escapeAttr(
    lot.id
  )}" title="${escapeAttr(title)}" aria-label="${escapeAttr(ariaLabel)}"${
    disabled ? ' disabled' : ''
  }><span>Price</span><span>Check</span>${costHint}</button>`;
}

/**
 * Build price review controls HTML
 */
export function buildPriceReviewControls(
  lot: Lot,
  state?: State | null,
  PRICE_REVIEW_SPINNER_TEMPLATE?: string
): string {
  if (!lot || !lot.id || !state) {
    return '';
  }
  const isPending = !!(
    state &&
    state.pendingPriceReviewIds &&
    lot.id &&
    state.pendingPriceReviewIds.has(lot.id)
  );
  const approveActive = lot.priceReview && lot.priceReview.status === 'successPrice';
  const retryActive = lot.priceReview && lot.priceReview.status === 'failedPrice';
  const lotLabel = lot.title ? lot.title : 'lot';
  const approveTitle = `Approve price for ${lotLabel}`;
  const retryTitle = `Request new analysis for ${lotLabel} (uses 1 credit)`;
  let htmlClass = 'price-review-controls';
  let html = '';
  if (isLotClosed(lot, getEffectiveCloseTime)) {
    htmlClass += ' price-review-controls--closed';
    html = `<div class="${htmlClass}" data-lot-id="${escapeAttr(lot.id)}">${buildClosedLotButton(lot, state)}</div>`;
    return html;
  }
  html = `<div class="${htmlClass}" data-lot-id="${escapeAttr(lot.id)}">`;
  const canRequestPriceCheck = state.mode === 'auth0' && !!lot.remote;
  const hasReviewStatus = !!(lot.priceReview && typeof lot.priceReview.status === 'string');
  const hasPricing = hasPricingData(lot);
  if (isPending) {
    const spinner =
      PRICE_REVIEW_SPINNER_TEMPLATE || '<span class="spinner" aria-hidden="true"></span>';
    html += `<div class="price-review-loading" role="status" aria-live="polite">${spinner}<span>Checking price…</span></div>`;
  } else if (!canRequestPriceCheck) {
    const disabledTitle =
      state.mode === 'auth0'
        ? 'Sync this lot to request AI pricing'
        : 'Sign in to request AI pricing';
    html += buildPriceCheckButton(lot, { disabled: true, title: disabledTitle });
  } else if (!hasReviewStatus && !hasPricing) {
    html += buildPriceCheckButton(lot, { title: 'Request new AI price analysis (uses 1 credit)' });
  } else if (lot.priceReview && lot.priceReview.approved) {
    html += `<div class="price-review-approved" title="Price approved" aria-label="Price approved"><span class="approved-checkmark">✓</span></div>`;
  } else {
    html += `<button type="button" class="price-review-button" data-variant="approve" data-action="price-ack" data-id="${escapeAttr(
      lot.id
    )}" aria-pressed="${approveActive ? 'true' : 'false'}" aria-label="${escapeAttr(
      truncateText(approveTitle, 80)
    )}" title="${escapeAttr(truncateText(approveTitle, 80))}">👍 <span>Approve</span></button>`;
    html += `<button type="button" class="price-review-button" data-variant="retry" data-action="price-retry" data-id="${escapeAttr(
      lot.id
    )}" aria-pressed="${retryActive ? 'true' : 'false'}" aria-label="${escapeAttr(
      truncateText(retryTitle, 80)
    )}" title="${escapeAttr(truncateText(retryTitle, 80))}">👎 <span>Retry</span></button>`;
  }
  html += '</div>';
  return html;
}

/**
 * Build lot card HTML for card view
 */
export function buildLotCardHtml(lot: Lot): string {
  if (!lot) return '';
  // Lightweight render debug: expose state about last rendered lot count
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w: Window & {
      __LOT_DASHBOARD__?: { formatCurrency?: (amount: number) => string };
      __LL_RENDERED_IDS?: string[];
    } = window;
    if (Array.isArray(w.__LL_RENDERED_IDS)) {
      w.__LL_RENDERED_IDS.push(lot.id);
    } else {
      w.__LL_RENDERED_IDS = [lot.id];
    }
  } catch {
    /* noop */
  }
  const imageList = collectLotImages(lot);
  // Filter out archived images
  const validImages = imageList.filter(entry => {
    const canonical = entry && (entry.canonicalUrl || entry.displayUrl);
    const display = entry && (entry.displayUrl || entry.canonicalUrl);
    return !__isArchived(canonical || display);
  });
  const imageCount = validImages.length;
  let imageUrl = '';
  let canonicalUrl = '';

  if (imageCount > 0) {
    const firstEntry = validImages[0];
    const displaySrc =
      firstEntry && (firstEntry.displayUrl || firstEntry.canonicalUrl)
        ? firstEntry.displayUrl || firstEntry.canonicalUrl
        : '';
    canonicalUrl =
      firstEntry && (firstEntry.canonicalUrl || firstEntry.displayUrl)
        ? firstEntry.canonicalUrl || firstEntry.displayUrl || ''
        : displaySrc;
    imageUrl = displaySrc || '';
  }

  let nextBid = '-';
  if (lot.nextRequiredBid != null) {
    const nextAmount = Number(lot.nextRequiredBid);
    const formattedNextBid = formatCurrencyDisplay(nextAmount);
    nextBid = formattedNextBid != null ? formattedNextBid : `$${String(lot.nextRequiredBid)}`;
  } else if (lot.nextRequiredBidRaw) {
    nextBid = escapeHtml(String(lot.nextRequiredBidRaw));
  }

  // Calculate Meat on the Bone if estimates exist
  const settings = ensureSettings();

  // Check if user has bid on this lot (compute once, used for all display variants)
  const hasBid = !!lot.bidding;
  const bidAmount = typeof lot.bidAmount === 'number' ? lot.bidAmount : null;
  const currentBidAmount = parseCurrencyValue(
    lot.currentBid != null ? lot.currentBid : lot.currentBidRaw
  );

  // User is winning if they have a bid and it's >= current bid
  const isWinning =
    hasBid && bidAmount != null && currentBidAmount != null && bidAmount >= currentBidAmount;

  const winningEmoji = isWinning
    ? '<span class="meat-badge__trophy" aria-label="Winning">🏆</span>'
    : '';

  let estimateButtonOrBadge = '';
  const hasLowEstimate = lot.lowSellValue != null && Number.isFinite(Number(lot.lowSellValue));
  const hasHighEstimate = lot.highSellValue != null && Number.isFinite(Number(lot.highSellValue));

  if (hasLowEstimate || hasHighEstimate) {
    // Use centralized meat-on-bone calculation
    const meatCalc = calculateMeatOnBone(lot as any, settings);
    const meatLow = meatCalc?.meatLow ?? null;
    const meatHigh = meatCalc?.meatHigh ?? null;
    const meatLowDisplay = formatMeatOnBoneValue(meatLow ?? 0);
    const meatHighDisplay = formatMeatOnBoneValue(meatHigh ?? 0);

    if (meatLowDisplay !== '—' || meatHighDisplay !== '—') {
      const biddingClass = hasBid ? ' lot-card__meat-badge--has-bid' : '';

      const meatRange = `${winningEmoji}<div class="meat-badge__value">${meatLowDisplay}</div><div class="meat-badge__separator">⋯</div><div class="meat-badge__value">${meatHighDisplay}</div>`;
      const badgeColor = getMeatBadgeColor(meatLow, meatHigh);
      estimateButtonOrBadge = `<div class="lot-card__meat-badge lot-card__meat-badge--${badgeColor}${biddingClass}" data-action="edit-estimate-mobile" data-id="${escapeAttr(
        lot.id
      )}" role="button" tabindex="0" title="Edit price estimates" aria-label="Edit price estimates">${meatRange}</div>`;
    } else {
      // Has estimates but no valid meat calculation - show dollar button with winning indicator
      const biddingClass = hasBid ? ' lot-card__estimate-button--has-bid' : '';
      estimateButtonOrBadge = `<div class="lot-card__estimate-button${biddingClass}" data-action="edit-estimate-mobile" data-id="${escapeAttr(
        lot.id
      )}" role="button" tabindex="0" title="Edit price estimates" aria-label="Edit price estimates">${winningEmoji}💵</div>`;
    }
  } else {
    // No estimates - show dollar button to add them with winning indicator
    const biddingClass = hasBid ? ' lot-card__estimate-button--has-bid' : '';
    estimateButtonOrBadge = `<div class="lot-card__estimate-button${biddingClass}" data-action="edit-estimate-mobile" data-id="${escapeAttr(
      lot.id
    )}" role="button" tabindex="0" title="Add price estimates" aria-label="Add price estimates">${winningEmoji}💵</div>`;
  }

  // Compact walk-away line (stay-under / never-exceed ceilings)
  let walkAwayLine = '';
  const walkAwayRange = calculateWalkAwayRange(lot as any, settings);
  if (
    walkAwayRange &&
    (walkAwayRange.stayUnderBid != null || walkAwayRange.neverExceedBid != null)
  ) {
    const fmt = (v: number | null) => (v != null ? (formatCurrencyDisplay(v) ?? `$${v}`) : null);
    const parts: string[] = [];
    const stayUnder = fmt(walkAwayRange.stayUnderBid);
    const neverExceed = fmt(walkAwayRange.neverExceedBid);
    if (stayUnder) parts.push(`Stay under ${stayUnder}`);
    if (neverExceed) parts.push(`Max ${neverExceed}`);
    const position = classifyLotBidPosition(lot as any, settings);
    const positionClass = position ? ` lot-card__walkaway--${position}` : '';
    walkAwayLine = `<div class="lot-card__walkaway${positionClass}">${escapeHtml(parts.join(' · '))}</div>`;
  }

  return (
    `<div class="lot-card" data-action="view-details" data-id="${escapeAttr(
      lot.id
    )}" title="View details for ${escapeAttr(lot.title)}">` +
    '<div class="lot-card__image-link">' +
    `<img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(
      lot.title || 'Lot image'
    )}" class="lot-card__image" loading="lazy" data-image-url="${escapeAttr(
      canonicalUrl
    )}" data-lot-id="${escapeAttr(lot.id)}">` +
    `<div class="lot-card__next-bid">Next Bid: ${nextBid}</div>` +
    walkAwayLine +
    estimateButtonOrBadge +
    '</div>' +
    '</div>'
  );
}
